import React from 'react'
import { TouchableOpacity, View } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { Feature, useFeatureFlag } from '@components'
import { Icon, Text } from '@components/base'
import { LinearGradient } from '@src/components/LinearGradient'
import { useNoraStreamChat } from '@src/screens/Nora/context/NoraStreamChatContext'
import { NoraVoiceModeSource } from '@src/screens/Nora/constants'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useIsDarkMode } from '@src/config/theme'
import {
  DARK_CARD_BACKGROUND_COLOR,
  DARK_GRADIENT_PROPS,
  LIGHT_GRADIENT_PROPS,
} from '@src/screens/Nora/constants/noraActionsGradient'

const NORA_INITIAL_PROMPT = 'Ask me anything'
// 25% alpha, as an 8-digit hex suffix like `palette.opacity.*`.
const DARK_HALO_ALPHA_SUFFIX = '40'

export const NoraVoiceHighlightCard = () => {
  const styles = useStyleSheet(themedStyles)
  const theme = useTheme()
  const isDarkMode = useIsDarkMode()
  const enabledByFlag = useFeatureFlag(Feature.NoraRealtimeVoiceMode)
  const { onAsk } = useNoraStreamChat()

  if (!enabledByFlag) {
    return null
  }

  const handlePress = () => {
    onAsk({
      initialPrompt: NORA_INITIAL_PROMPT,
      initialVoiceModeEnabled: true,
      initialVoiceModeSource: NoraVoiceModeSource.HomeVoiceCard,
    })
  }

  const gradientProps = isDarkMode ? DARK_GRADIENT_PROPS : LIGHT_GRADIENT_PROPS

  // The halo is the dark primary at 25%. In dark mode `theme.primary.base`
  // already resolves to it, so the alpha suffix is all that's added.
  const darkHaloStyle = {
    backgroundColor: `${theme['theme.primary.base']}${DARK_HALO_ALPHA_SUFFIX}`,
  }

  return (
    <TouchableOpacity
      accessibilityLabel="Talk to Nora"
      onPress={handlePress}
      style={[
        styles.container,
        isDarkMode && {
          backgroundColor: DARK_CARD_BACKGROUND_COLOR,
        },
      ]}
    >
      <LinearGradient
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        {...gradientProps}
        style={styles.gradient}
      />
      <View
        testID="nora-voice-highlight-halo"
        style={[styles.fabShadow, isDarkMode && darkHaloStyle]}
      >
        <View testID="nora-voice-highlight-fab" style={[styles.fab, isDarkMode && styles.fabDark]}>
          <Icon name="wave-form" weight="bold" size={22} style={styles.fabIcon} />
        </View>
      </View>
      <Text type="regular" bold lineSpacing="none" style={styles.title}>
        Talk to Nora
      </Text>
      <Text type="small" lineSpacing="none" style={styles.subtitle}>
        Ask me anything
      </Text>
    </TouchableOpacity>
  )
}

const themedStyles = StyleService.create({
  container: {
    width: 148,
    height: 136,
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'theme.surface.base',
    overflow: 'hidden',
    paddingTop: 20,
    backgroundColor: 'theme.solid.white',
  },
  gradient: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  fabShadow: {
    width: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 24,
    backgroundColor: 'theme.surface.base1',
  },
  fab: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 20,
    backgroundColor: 'theme.primary.base',
  },
  fabDark: {
    shadowColor: 'theme.marketing.onText',
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.25,
    shadowRadius: 11,
    elevation: 5,
  },
  fabIcon: {
    color: 'theme.solid.white',
  },
  title: {
    marginTop: 12,
    color: 'theme.text.primary',
    textAlign: 'center',
    letterSpacing: 0.15,
  },
  subtitle: {
    marginTop: 8,
    color: 'theme.text.tertiary',
    textAlign: 'center',
  },
})
